import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CircleAlert, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { Pagination } from '../components/Pagination';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../features/auth/AuthProvider';
import { holdsKey, listHolds, type Hold } from '../features/evidence/api';
import { ReleaseDialog } from '../features/evidence/dialogs';
import { CopiesPanel } from '../features/evidence/CopiesPanel';
import { ExportsPanel } from '../features/evidence/ExportsPanel';
import { formatRange } from '../features/evidence/format';
import { dayOf } from '../features/playback/time';
import { formatBytes } from '../features/system/media';
import { readPaging } from '../lib/paging';
import { useTimeFormat } from '../lib/useTimeFormat';
import { useUrlState } from '../lib/useUrlState';

const filters = [
  { id: 'active', label: 'Ativas' },
  { id: 'all', label: 'Todas' },
] as const;

function HoldsPanel() {
  const { request, can, session } = useAuth();
  const tz = session?.tenant.timezone;
  const fmt = useTimeFormat();
  const [params, update] = useUrlState();
  const { page, size } = readPaging(params);
  const scope = params.get('scope') === 'all' ? 'all' : 'active';
  const canRelease = can('recordings.manage');
  const [releasing, setReleasing] = useState<Hold | null>(null);

  const holds = useQuery({
    queryKey: [...holdsKey, 'page', { scope, page, size }],
    queryFn: () => listHolds(request, { active: scope === 'active', page, size }),
    placeholderData: keepPreviousData,
  });

  const range = (h: Hold) => formatRange(h.from, h.to, tz);

  return (
    <>
      <div className="space-y-3">
        <div role="group" aria-label="Filtrar evidências" className="flex gap-1">
          {filters.map((f) => (
            <Button
              key={f.id}
              aria-pressed={scope === f.id}
              className={scope === f.id ? 'border-accent! text-fg!' : ''}
              onClick={() => {
                update({ scope: f.id === 'active' ? '' : f.id, page: '' });
              }}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          {holds.isError && !holds.data ? (
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar as evidências"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button variant="primary" onClick={() => void holds.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          ) : holds.data?.items.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title={
                scope === 'active' ? 'Nenhuma evidência ativa' : 'Nenhuma evidência registrada'
              }
              description="Na tela de Reprodução, use “Proteger evidência” para preservar um trecho além da retenção."
            />
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full min-w-[820px] text-left text-[13px]"
                aria-busy={holds.isFetching}
              >
                <caption className="sr-only">Evidências da organização</caption>
                <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
                  <tr className="border-b border-line">
                    <th scope="col" className="px-4 py-2 font-medium">
                      Câmera e intervalo
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Motivo
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Responsável
                    </th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">
                      Gravações
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Situação
                    </th>
                    <th scope="col" className="w-px px-4 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!holds.data
                    ? Array.from({ length: 5 }, (_, i) => (
                        <tr
                          key={i}
                          aria-hidden="true"
                          className="border-b border-line last:border-0"
                        >
                          {['w-48', 'w-40', 'w-24', 'ml-auto w-12', 'w-16', 'w-10'].map((w) => (
                            <td key={w} className="px-4 py-3.5">
                              <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : holds.data.items.map((h) => (
                        <tr
                          key={h.id}
                          className="border-b border-line align-top last:border-0 hover:bg-surface-hover/50"
                        >
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-fg">
                              {h.device.name || (
                                <span className="text-fg-subtle">Câmera excluída</span>
                              )}
                            </div>
                            <div className="font-mono text-xs text-fg-subtle">{range(h)}</div>
                          </td>
                          <td className="max-w-xs px-4 py-2.5 text-fg-muted">
                            <p className="line-clamp-2 break-words">{h.reason}</p>
                            {!h.active && h.release_reason && (
                              <p className="mt-0.5 line-clamp-2 text-xs break-words text-fg-subtle">
                                Liberada: {h.release_reason}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-fg-muted">
                            <div>{h.created_by.name}</div>
                            <div className="text-xs text-fg-subtle">
                              {fmt.absolute(h.created_at)}
                            </div>
                            {h.released_by && (
                              <div className="mt-0.5 text-xs text-fg-subtle">
                                liberada por {h.released_by.name}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs text-fg-muted tabular-nums">
                            <div>
                              {h.segments} {h.segments === 1 ? 'trecho' : 'trechos'}
                            </div>
                            <div className="text-fg-subtle">{formatBytes(h.bytes)}</div>
                          </td>
                          <td className="px-4 py-2.5">
                            {h.status === 'released' ? (
                              <StatusBadge tone="neutral">Liberada</StatusBadge>
                            ) : h.status === 'expired' ? (
                              <StatusBadge tone="neutral">Vencida</StatusBadge>
                            ) : (
                              <StatusBadge tone="warn">Protegida</StatusBadge>
                            )}
                            {h.expires_at && h.status !== 'released' && (
                              <div className="mt-1 text-xs whitespace-nowrap text-fg-subtle">
                                {h.status === 'expired' ? 'venceu em' : 'até'}{' '}
                                {fmt.absolute(h.expires_at)}
                              </div>
                            )}
                            {!h.expires_at && h.status === 'active' && (
                              <div className="mt-1 text-xs whitespace-nowrap text-fg-subtle">
                                sem validade
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right whitespace-nowrap">
                            {h.device.name && (
                              <Link
                                to={`/playback?device=${encodeURIComponent(h.device.id)}&day=${dayOf(Date.parse(h.from), tz)}`}
                                className="mr-1 inline-flex h-7 items-center rounded-md px-2 text-[13px] text-accent hover:bg-surface-hover"
                              >
                                Reproduzir
                              </Link>
                            )}
                            {h.status !== 'released' && canRelease && (
                              <Button
                                variant="ghost"
                                className="h-7"
                                aria-label={`Liberar evidência de ${h.device.name || 'câmera excluída'}`}
                                onClick={() => {
                                  setReleasing(h);
                                }}
                              >
                                Liberar
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
          {holds.data && holds.data.total > 0 && (
            <Pagination
              page={page}
              size={size}
              total={holds.data.total}
              onPage={(p) => {
                update({ page: String(p) });
              }}
              onSize={(s) => {
                update({ size: String(s) });
              }}
            />
          )}
        </div>
      </div>
      {releasing && (
        <ReleaseDialog
          hold={releasing}
          onClose={() => {
            setReleasing(null);
          }}
        />
      )}
    </>
  );
}

const tabs = [
  { id: 'holds', label: 'Proteções' },
  { id: 'exports', label: 'Exportações' },
  { id: 'copies', label: 'Cópias do equipamento' },
] as const;

type TabId = (typeof tabs)[number]['id'];

export function EvidencePage() {
  const [params, update] = useUrlState();
  const tab: TabId = tabs.find((t) => t.id === params.get('tab'))?.id ?? 'holds';
  return (
    <>
      <PageHeader
        title="Evidências"
        description="Trechos protegidos da retenção, arquivos exportados e cópias dos gravadores, com motivo e responsável."
      />
      <div className="space-y-4 p-4 sm:p-6">
        <div role="tablist" aria-label="Evidências" className="flex gap-4 border-b border-line">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`tab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls={`panel-${t.id}`}
              onClick={() => {
                update({ tab: t.id === 'holds' ? '' : t.id, page: '', scope: '' });
              }}
              className={`-mb-px border-b-2 px-1 pb-2 text-[13px] font-medium transition-colors ${
                tab === t.id
                  ? 'border-accent text-fg'
                  : 'border-transparent text-fg-muted hover:text-fg'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
          {tab === 'holds' ? (
            <HoldsPanel />
          ) : tab === 'exports' ? (
            <ExportsPanel />
          ) : (
            <CopiesPanel />
          )}
        </div>
      </div>
    </>
  );
}
